import { useMemo } from 'react';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockPartners } from '@/mocks/partners';
import { mockDisputes } from '@/mocks/disputes';
import { mockVerticals } from '@/mocks/verticals';
import { mockLeads } from '@/mocks/leads';
import { mockOffers } from '@/mocks/offers';
import { Wallet, TrendingUp, AlertTriangle } from 'lucide-react';

export default function FinanceSummaryPage() {
  const summary = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const monthPayouts = mockPayoutLines.filter(p => {
      const d = new Date(p.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });

    const totalGross = monthPayouts.reduce((sum, p) => sum + p.amount_eur, 0);
    const byVertical: Record<string, number> = {};
    const byPartner: Record<string, number> = {};

    monthPayouts.forEach(p => {
      const offer = mockOffers.find(o => o.id === p.offer_id);
      const vertical = mockVerticals.find(v => v.id === offer?.vertical_id);
      const partner = mockPartners.find(pt => pt.id === p.partner_id);
      const vName = vertical?.name || 'Unknown';
      const pName = partner?.display_name || p.partner_id;
      byVertical[vName] = (byVertical[vName] || 0) + p.amount_eur;
      byPartner[pName] = (byPartner[pName] || 0) + p.amount_eur;
    });

    const disputed = monthPayouts.filter(p => p.status === 'disputed' || p.status === 'reversed');
    const disputeRate = monthPayouts.length > 0 ? Math.round((disputed.length / monthPayouts.length) * 100) : 0;

    return { totalGross, byVertical, byPartner, disputeRate, monthPayouts };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Financial Summary</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>P&L overview for this month</p>
      </div>

      {/* Top cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border p-5 transition-all duration-300"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Gross Payout This Month</p>
              <p className="text-2xl font-bold mt-1.5" style={{ color: 'var(--text-primary)' }}>€{summary.totalGross.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{ background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--success-border)' }}
            >
              <Wallet size={18} />
            </div>
          </div>
        </div>
        <div className="rounded-xl border p-5 transition-all duration-300"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Payout Lines</p>
              <p className="text-2xl font-bold mt-1.5" style={{ color: 'var(--text-primary)' }}>{summary.monthPayouts.length}</p>
            </div>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
            >
              <TrendingUp size={18} />
            </div>
          </div>
        </div>
        <div className="rounded-xl border p-5 transition-all duration-300"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Dispute Rate</p>
              <p className="text-2xl font-bold mt-1.5" style={{ color: 'var(--text-primary)' }}>{summary.disputeRate}%</p>
            </div>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}
            >
              <AlertTriangle size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl border overflow-hidden"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <div className="px-5 py-3.5 border-b" style={{ borderColor: 'var(--border-separator)' }}>
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>By Vertical</h2>
          </div>
          <div>
            {Object.entries(summary.byVertical).sort((a, b) => b[1] - a[1]).map(([name, amount], i, arr) => (
              <div key={name} className="px-5 py-3 flex items-center justify-between transition-colors hover-surface"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border-separator)' : undefined }}
              >
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{name}</span>
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>€{amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border overflow-hidden"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <div className="px-5 py-3.5 border-b" style={{ borderColor: 'var(--border-separator)' }}>
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>By Partner</h2>
          </div>
          <div>
            {Object.entries(summary.byPartner).sort((a, b) => b[1] - a[1]).map(([name, amount], i, arr) => (
              <div key={name} className="px-5 py-3 flex items-center justify-between transition-colors hover-surface"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border-separator)' : undefined }}
              >
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{name}</span>
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>€{amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}